import { useMemo } from 'react';
import {
  ListPersonsByPhoneLegacyRequest,
  SearchPersonsLegacyRequest,
} from '@weave/schema-gen-ts/dist/schemas/persons/v3/persons_service.pb';
import { isPartialPhoneNumber, isPhoneNumber, removeNonDigits } from '@frontend/phone-numbers';
import {
  useListPersonsByPhoneLegacyQuery,
  useSearchPersonsByPMIDLegacyQuery,
  useSearchPersonsLegacyPostQuery,
} from './infinite-queries';

const MIN_PHONE_SEARCH_LENGTH = 4;
export type FullPersonSearchProps = SearchPersonsLegacyRequest;

/**
 * Combines multiple person search queries, based on search term.
 * - 1.A) if the search term is a phone number, it uses ListPersonsByPhoneLegacy.
 * - 1.B) otherwise, it uses SearchPersonsLegacyPost.
 * - 2) If the search is one word it also queries SearchPersonsByPMIDLegacy.
 *
 * The results are merged [...2, ...1], de-duplicated by personPmid, and returned.
 *
 * Props standardized to match SearchPersonsLegacyPost.
 */
export const useConvolutedInsanityCombinedPersonV3Search = (props: FullPersonSearchProps) => {
  const enablePhoneSearch =
    (isPhoneNumber(props.search) || (!!props.search && isPartialPhoneNumber(props.search))) &&
    removeNonDigits(props.search).length >= MIN_PHONE_SEARCH_LENGTH;

  const hasSearch = !!props.search && props.search.trim().length > 0;
  const enablePmidSearch = !!props.search && !props.search.trim().includes(' ');

  const phoneProps = useMemo(() => {
    return {
      locationIds: props.locationIds,
      phoneNumber: removeNonDigits(props.search),
      partialSearch: true, // partial search doesn't work. Just leave it always true. Will still only match on 10 digits.
      sourceIds: props.sourceIds,
      page: props.page,
    } satisfies ListPersonsByPhoneLegacyRequest;
  }, [props]);

  const personsQuery = useSearchPersonsLegacyPostQuery(
    {
      ...props,
    },
    {
      enabled: hasSearch && !enablePhoneSearch,
    }
  );

  const personsPhoneQuery = useListPersonsByPhoneLegacyQuery(
    {
      ...phoneProps,
    },
    {
      enabled: hasSearch && enablePhoneSearch,
    }
  );

  const pmidQuery = useSearchPersonsByPMIDLegacyQuery(
    {
      ...props,
    },
    {
      enabled: hasSearch && enablePmidSearch,
    }
  );

  const baseQuery = enablePhoneSearch ? personsPhoneQuery : personsQuery;

  // if we have results from pmidQuery, there is a chance that the same person is returned from both queries, so we need to filter out duplicates
  // if there are no results from pmidQuery, we can just use the results from personsQuery
  const uniqueResults = useMemo(() => {
    const pmidQueryPersons = pmidQuery.data?.persons || [];
    const baseQueryPersons = baseQuery.data?.persons || [];

    return pmidQuery.data?.persons.length
      ? [
          ...new Map(
            [...pmidQueryPersons, ...baseQueryPersons].map((person) => [person['personPmid'], person])
          ).values(),
        ]
      : baseQueryPersons;
  }, [baseQuery.data, pmidQuery.data]);

  return {
    isLoading: baseQuery.isLoading || (enablePmidSearch ? pmidQuery.isLoading : false),
    isError: baseQuery.isError || (enablePmidSearch ? pmidQuery.isError : false),
    isFetched: baseQuery.isFetched || (enablePmidSearch ? pmidQuery.isFetched : false),
    isSuccess: baseQuery.isSuccess || (enablePmidSearch ? pmidQuery.isSuccess : false),
    error: baseQuery.error || (enablePmidSearch ? pmidQuery.error : null),
    persons: uniqueResults || [],

    /**
     * Either the active person search or phone search query
     */
    baseQuery,

    /**
     * The PMID search query
     */
    pmidQuery,
  };
};
